// Cross-cutting formatters. Money is stored as integer minor units; all user-facing
// dates render DD/MM/YYYY in the single business timezone.

/** Format integer minor units (e.g. paisa) as a currency string, e.g. 150000 -> "PKR 1,500.00". */
export function formatMoney(minorUnits: number, currency = 'PKR', minorPerMajor = 100): string {
  const negative = minorUnits < 0;
  const abs = Math.abs(minorUnits);
  const major = Math.floor(abs / minorPerMajor);
  const minor = abs % minorPerMajor;
  const fractionDigits = String(minorPerMajor - 1).length; // 100 -> 2, 1000 -> 3
  const majorStr = major.toLocaleString('en-US');
  const minorStr = String(minor).padStart(fractionDigits, '0');
  return `${negative ? '-' : ''}${currency} ${majorStr}.${minorStr}`;
}

/** Parse a major-unit input (e.g. "1500.50") into integer minor units. */
export function toMinorUnits(major: number, minorPerMajor = 100): number {
  return Math.round(major * minorPerMajor);
}

/** Render a byte count as a human-readable size, e.g. 1536 -> "1.5 KB", 0 -> "0 B". */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/** The single business timezone the whole app renders dates in. */
export const BUSINESS_TZ = 'Asia/Karachi';

// en-GB gives DD/MM/YYYY ordering; timeZone pins output to the business tz for every viewer.
function fmt(iso: string, opts: Intl.DateTimeFormatOptions, tz: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: tz }).format(date);
}

/** Render an ISO date as DD/MM/YYYY in the business timezone. '—' for invalid input. */
export function formatDate(iso: string | null | undefined, tz: string = BUSINESS_TZ): string {
  if (!iso) return '-';
  return fmt(iso, { day: '2-digit', month: '2-digit', year: 'numeric' }, tz) ?? '-';
}

/** Render an ISO date-time as DD/MM/YYYY, HH:mm (24h) in the business timezone. */
export function formatDateTime(iso: string | null | undefined, tz: string = BUSINESS_TZ): string {
  if (!iso) return '-';
  return (
    fmt(
      iso,
      { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
      tz,
    ) ?? '-'
  );
}
